import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PaystackCheckoutButton from "../payments/PaystackCheckoutButton";
import { hasClaimedFreeStarterListing, recordJobListingPurchase } from "../../services/listingPurchases";
import useListingCredits from "../../hooks/useListingCredits";

const defaultOptions = Array.from({ length: 10 }, (_, idx) => idx + 1);

const JobListingPurchasePanel = ({
    pricePerListing = 749,
    listingOptions = defaultOptions,
    onPurchaseRecorded,
    jobListingPurchases,
}) => {
    const { user } = useAuth();
    const { credits, loading: creditsLoading } = useListingCredits();
    const [selectedCount, setSelectedCount] = useState(listingOptions[0]);
    const [status, setStatus] = useState(null);
    const [starterBonusAvailable, setStarterBonusAvailable] = useState(false);
    const [checkingBonus, setCheckingBonus] = useState(true);

    const totalPrice = pricePerListing * selectedCount;
    const totalListingsAwarded = selectedCount + (starterBonusAvailable ? 1 : 0);

    useEffect(() => {
        let isMounted = true;

        const evaluateBonusEligibility = async () => {
            if (!user?.id) {
                setStarterBonusAvailable(false);
                setCheckingBonus(false);
                return;
            }

            setCheckingBonus(true);
            try {
                const hasClaimed = await hasClaimedFreeStarterListing(user.id);
                if (isMounted) {
                    setStarterBonusAvailable(!hasClaimed);
                }
            } catch (err) {
                console.error("Unable to verify free starter listing eligibility:", err);
                if (isMounted) {
                    setStarterBonusAvailable(false);
                }
            } finally {
                if (isMounted) {
                    setCheckingBonus(false);
                }
            }
        };

        evaluateBonusEligibility();

        return () => {
            isMounted = false;
        };
    }, [user?.id]);

    const handleSuccess = async (paystackResponse) => {
        setStatus({ type: "info", message: "Recording your purchase..." });
        try {
            const record = await recordJobListingPurchase({
                employerId: user?.id,
                listingCount: selectedCount,
                amountPaid: totalPrice,
                currency: "ZAR",
                paystackReference: paystackResponse.reference,
                paystackPayload: paystackResponse,
                includeStarterBonus: starterBonusAvailable,
            });

            if (record?.free_bonus_awarded) {
                setStarterBonusAvailable(false);
            }

            const awardedListings = record?.listing_count ?? totalListingsAwarded;
            setStatus({
                type: "success",
                message: `Payment captured. ${awardedListings} listing credit${awardedListings > 1 ? "s" : ""} added to your account${
                    record?.free_bonus_awarded ? " (includes your Free Starter job ad)" : ""
                }.`,
            });

            onPurchaseRecorded?.({
                listingCount: awardedListings,
                amountPaid: totalPrice,
                reference: paystackResponse.reference,
            });
        } catch (err) {
            console.error("Failed to record job listing purchase:", err);
            setStatus({
                type: "error",
                message: err?.message || "Payment succeeded but we could not save it. Please contact support with your reference.",
            });
        }
    };

    const handleError = (message) => {
        console.error("[JobListingPurchasePanel] Paystack error:", message);
        setStatus({
            type: "error",
            message: message || "Payment failed or was cancelled.",
        });
    };

    if (!user) {
        return (
            <div className="rounded-xl border border-gray-200 p-4 text-sm text-gray-600 bg-gray-50">
                Please sign in as an employer to purchase job listings.
            </div>
        );
    }

    return (
        <div>
            <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Job listings
                </label>
                <select
                    value={selectedCount}
                    onChange={(event) => setSelectedCount(Number(event.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                >
                    {listingOptions.map((option) => (
                        <option key={option} value={option}>
                            {option} Job listing{option > 1 ? "s" : ""}
                        </option>
                    ))}
                </select>
            </div>

            <div className="mb-4">
                <div className="text-2xl font-bold text-black mb-1.5">
                    {`R${totalPrice.toLocaleString("en-ZA")}`}
                </div>
                <div className="text-xs text-gray-600">
                    {selectedCount > 1 ? `/ ${selectedCount} listings per month` : "/ Per Month"}
                </div>
                <div className="text-xs text-gray-700 mt-1">
                    {starterBonusAvailable
                        ? `Includes ${totalListingsAwarded} total listings (${selectedCount} paid + 1 Free Starter job ad)`
                        : `${totalListingsAwarded} listing${totalListingsAwarded > 1 ? "s" : ""} delivered`}
                </div>
            </div>

            {starterBonusAvailable && (
                <div className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-xs text-green-800">
                    Free Starter benefit: your first job ad (visible for 5 days) will be added automatically once you
                    complete this checkout.
                </div>
            )}

            {status && (
                <div
                    className={`mb-3 rounded-lg px-4 py-2 text-sm ${
                        status.type === "success"
                            ? "bg-green-100 text-green-800"
                            : status.type === "error"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-700"
                    }`}
                >
                    {status.message}
                </div>
            )}

            {credits && (
                <div className="mb-3 rounded-lg px-4 py-2 text-sm bg-red-100 text-red-800">
                    {credits.remainingListings >= 999999
                        ? "You have unlimited job listings available."
                        : `You have ${credits.remainingListings} job listing${
                            credits.remainingListings === 1 ? "" : "s"
                        } remaining.`}
                </div>
            )}
            {!credits && creditsLoading && (
                <div className="mb-3 rounded-lg px-4 py-2 text-sm bg-gray-100 text-gray-700">
                    Checking plan status...
                </div>
            )}

            <PaystackCheckoutButton
                amount={totalPrice}
                disabled={checkingBonus}
                referencePrefix="job_listing"
                metadata={{
                    listing_count: selectedCount,
                    price_per_listing: pricePerListing,
                }}
                customFields={[
                    {
                        display_name: "Job listings",
                        variable_name: "listing_count",
                        value: String(selectedCount),
                    },
                ]}
                contextData={{
                    listingCount: selectedCount,
                    totalPrice,
                    starterBonusApplied: starterBonusAvailable,
                }}
                onSuccess={handleSuccess}
                onError={handleError}
                className="w-full bg-black text-white rounded-full py-3 font-medium hover:opacity-90 transition-opacity"
            >
                {jobListingPurchases && jobListingPurchases.length > 0 ? "Add More Job Listings" : "Create Job Listing"}
            </PaystackCheckoutButton>
        </div>
    );
};

export default JobListingPurchasePanel;

